import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Compass, RotateCcw, Rocket, X, MailX, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Lock } from "lucide-react";
import { Btn, Card, Tag, cx } from "./kit";
import { ConfirmDialog } from "./common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../lib/auth";
import { TOKEN_KEY } from "../lib/api";
import { DEMO_ROLES, getDemo, patchDemo, resetDemo, switchDemoRole, onSimulated, clearDemo } from "../lib/demo";

const STEPS = [
  { key: "welcome", role: "groomer", to: () => "/home" },
  { key: "dog", role: "groomer", to: (ids) => `/dogs/${ids.dog}` },
  { key: "programme", role: "groomer", to: (ids) => `/programmes/${ids.programme}` },
  { key: "visit", role: "groomer", to: (ids) => `/visits/${ids.visit}` },
  { key: "send", role: "groomer", to: (ids) => `/programmes/${ids.programme}` },
  { key: "owner", role: "owner", to: () => "/owner" },
  { key: "review", role: "groomer", to: () => "/home" },
  { key: "end" },
];
const lng = (i18n) => (i18n.language?.startsWith("ru") ? "ru" : "en");

export function useStartRealWork() {
  return () => { clearDemo(); localStorage.removeItem(TOKEN_KEY); window.location.assign("/register"); };
}

function RoleSwitcher() {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const cur = getDemo()?.role || "groomer";
  const go = async (r) => {
    if (r.key === cur) return;
    switchDemoRole(r.key);
    await refresh();
    navigate(r.path);
  };
  return (
    <div role="group" aria-label={t("sandbox.role_label")} data-testid="demo-role-switcher" className="flex gap-1.5 overflow-x-auto pb-0.5">
      {DEMO_ROLES.map((r) => (
        <button key={r.key} data-testid={`demo-role-${r.key}`} disabled={!r.live} aria-pressed={cur === r.key} onClick={() => go(r)}
          className={cx("flex min-h-[36px] shrink-0 items-center gap-1 rounded-full border px-3 text-xs font-semibold transition-colors",
            cur === r.key ? "bg-[hsl(24_14%_18%)] text-white" : r.live ? "bg-white hover:bg-muted" : "bg-white/60 text-muted-foreground cursor-not-allowed")}>
          {!r.live && <Lock className="h-3 w-3" aria-hidden />}{t(`sandbox.roles.${r.key}`)}
          {!r.live && <span className="font-normal">· {t("sandbox.coming_later")}</span>}
        </button>
      ))}
    </div>
  );
}

function SimulatedDialog() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  useEffect(() => onSimulated(setItems), []);
  return (
    <Dialog open={!!items} onOpenChange={(o) => !o && setItems(null)}>
      <DialogContent data-testid="simulated-email-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MailX className="h-5 w-5" aria-hidden />{t("sandbox.simulated_title")}</DialogTitle>
          <DialogDescription>{t("sandbox.simulated_sub")}</DialogDescription>
        </DialogHeader>
        {(items || []).map((p, i) => (
          <div key={i} data-testid={`simulated-preview-${i}`} lang={p.language} className="rounded-xl border bg-muted/40 p-3 text-sm">
            <p className="text-xs text-muted-foreground">{t("confirm.recipient")}: {p.to} · {p.language?.toUpperCase()}</p>
            <p className="mt-1 font-semibold">{p.subject}</p>
            <p className="mt-1 whitespace-pre-wrap">{p.body}</p>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

function Guide({ onClose }) {
  const { t } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const startReal = useStartRealWork();
  const [step, setStep] = useState(getDemo()?.step || 0);
  const [min, setMin] = useState(!!getDemo()?.guide_min);
  const s = STEPS[step];
  const move = (n) => { setStep(n); patchDemo({ step: n }); };
  const minimise = (v) => { setMin(v); patchDemo({ guide_min: v }); };
  useEffect(() => {
    const c = document.documentElement.classList;
    c.toggle("demo-guide-on", !min);
    c.toggle("demo-guide-min", min);
    return () => c.remove("demo-guide-on", "demo-guide-min");
  }, [min]);
  const go = async () => {
    const d = getDemo();
    if (s.role && d.role !== s.role) { switchDemoRole(s.role); await refresh(); }
    navigate(s.to(d.ids));
  };
  if (min) {
    return (
      <button data-testid="demo-guide-expand" onClick={() => minimise(false)} aria-label={t("sandbox.expand")}
        className="fixed right-0 top-1/2 z-50 flex min-h-[48px] -translate-y-1/2 items-center gap-1.5 rounded-l-full bg-[hsl(24_14%_18%)] py-2 pl-3 pr-2 text-sm font-semibold text-white shadow-xl sm:bottom-5 sm:right-5 sm:top-auto sm:translate-y-0 sm:rounded-full sm:px-4">
        <Compass className="h-4 w-4 shrink-0" aria-hidden />
        <span className="sm:hidden">{step + 1}/{STEPS.length}</span>
        <span className="hidden max-w-[300px] truncate sm:inline">{t("sandbox.step_of", { n: step + 1, total: STEPS.length })} · {t(`sandbox.steps.${s.key}.title`)}</span>
        <ChevronUp className="hidden h-4 w-4 shrink-0 sm:block" aria-hidden />
      </button>
    );
  }
  return (
    <Card data-testid="demo-guide-panel" role="complementary" aria-label={t("sandbox.guide")}
      className="fixed inset-x-2 bottom-2 z-50 flex max-h-[25vh] flex-col p-3 shadow-xl sm:inset-x-auto sm:right-5 sm:bottom-5 sm:max-h-[70vh] sm:w-[380px] sm:p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="demo-guide-progress">{t("sandbox.step_of", { n: step + 1, total: STEPS.length })}</p>
        <div className="-mr-1 -mt-1 flex">
          <button data-testid="demo-guide-minimise" onClick={() => minimise(true)} aria-label={t("sandbox.minimise")} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"><ChevronDown className="h-4 w-4" /></button>
          <button data-testid="demo-guide-close" onClick={onClose} aria-label={t("sandbox.guide_close")} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="demo-guide-scroll">
      <h2 className="mt-0.5 font-display text-base font-bold sm:text-lg" data-testid="demo-guide-title">{t(`sandbox.steps.${s.key}.title`)}</h2>
      <p className="mt-1 text-sm" data-testid="demo-guide-body">{t(`sandbox.steps.${s.key}.body`)}</p>
      {s.key !== "end" && <p className="mt-2 rounded-lg bg-[hsl(var(--lavender))] p-2 text-xs" data-testid="demo-guide-consequence"><b>{t("sandbox.then")}</b> {t(`sandbox.steps.${s.key}.then`)}</p>}
      {s.key === "end" && (
        <div className="mt-3 space-y-2">
          <div data-testid="pricing-coming-later-card" className="rounded-xl border border-dashed p-3 text-sm"><Tag>{t("sandbox.coming_later")}</Tag><p className="mt-1 font-semibold">{t("sandbox.pricing")}</p></div>
          <Btn testid="guide-start-real-work-btn" className="w-full" onClick={startReal}><Rocket className="h-4 w-4" />{t("sandbox.start_real")}</Btn>
        </div>
      )}
      </div>
      <div className="mt-2 flex shrink-0 items-center gap-2">
        <Btn variant="outline" testid="demo-guide-back" className="min-h-[40px] px-3" disabled={step === 0} onClick={() => move(step - 1)} aria-label={t("common.back")}><ChevronLeft className="h-4 w-4" /></Btn>
        {s.to && <Btn variant="outline" testid="demo-guide-go" className="min-h-[40px] flex-1" onClick={go}>{t("sandbox.go_there")}</Btn>}
        {step < STEPS.length - 1 && <Btn testid="demo-guide-next" className="min-h-[40px] flex-1" onClick={() => move(step + 1)}>{t("sandbox.next")}<ChevronRight className="h-4 w-4" /></Btn>}
      </div>
    </Card>
  );
}

export default function DemoChrome() {
  const { t, i18n } = useTranslation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const startReal = useStartRealWork();
  const [guide, setGuide] = useState(getDemo()?.guide !== false);
  const [confirm, setConfirm] = useState(false);
  const toggle = (v) => { setGuide(v); patchDemo({ guide: v }); };
  const reset = async () => { await resetDemo(lng(i18n)); await refresh(); setGuide(true); navigate("/home"); };
  return (
    <>
      <div data-testid="demo-banner" role="region" aria-label={t("sandbox.banner_label")} className="border-b bg-[hsl(42_90%_88%)] text-[hsl(30_40%_18%)]">
        <div className="mx-auto max-w-6xl space-y-2 px-4 py-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <p className="text-sm font-bold" data-testid="demo-banner-text">{t("sandbox.banner")}</p>
            <p className="text-xs" data-testid="demo-reset-policy">{t("sandbox.reset_policy")}</p>
            <div className="ml-auto flex flex-wrap gap-1.5">
              {!guide && <Btn variant="outline" testid="demo-guide-open" className="min-h-[36px] px-3 text-xs" onClick={() => toggle(true)}><Compass className="h-4 w-4" />{t("sandbox.guide")}</Btn>}
              <Btn variant="outline" testid="demo-reset-btn" className="min-h-[36px] px-3 text-xs" onClick={() => setConfirm(true)}><RotateCcw className="h-4 w-4" />{t("sandbox.reset")}</Btn>
              <Btn testid="demo-start-real-work-btn" className="min-h-[36px] px-3 text-xs" onClick={startReal}><Rocket className="h-4 w-4" />{t("sandbox.start_real")}</Btn>
            </div>
          </div>
          <RoleSwitcher />
        </div>
      </div>
      {guide && <Guide onClose={() => toggle(false)} />}
      <SimulatedDialog />
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title={t("sandbox.reset_confirm_title")} consequence={t("sandbox.reset_confirm_body")} danger confirmLabel={t("sandbox.reset")} onConfirm={reset} testid="demo-reset-confirm" />
    </>
  );
}
